/**
 * Page publique `/infos` — FAQ + plan d'accès + horaires + adresse.
 *
 * Objectifs :
 *   * Donner toutes les infos pratiques aux convives en un seul endroit
 *     (le mail de confirmation pointe vers cette page) ;
 *   * Réduire les appels "vous êtes où ?" / "ça commence à quelle heure ?" /
 *     "il y a un parking ?" au club ;
 *   * Afficher le plan d'accès photo (6 parkings publics + 1 réservé bénévoles)
 *     fourni par le club, sans dépendance à une carte tierce (pas de tracking
 *     Google Maps / Mapbox).
 */
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import ChildCareOutlinedIcon from '@mui/icons-material/ChildCareOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalDiningOutlinedIcon from '@mui/icons-material/LocalDiningOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MusicNoteOutlinedIcon from '@mui/icons-material/MusicNoteOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import RestaurantOutlinedIcon from '@mui/icons-material/RestaurantOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { fetchCapacity } from '../api.js';

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

const FAQ = [
  {
    q: 'À quelle heure dois-je arriver ?',
    a: "L'accueil ouvre à 19h00, le service du repas commence vers 20h30. Si vous le pouvez, prévoyez d'arriver entre 19h et 20h pour fluidifier la caisse et éviter la file. À l'arrivée, passez à l'accueil / caisse pour régler (si besoin) et récupérer vos bracelets.",
  },
  {
    q: 'Comment se passe le repas ?',
    a: "Buffet à volonté de moules et de saucisses (au choix) accompagné de frites à volonté. Le dessert est servi quand vous le souhaitez, et limité à un par convive : vous remettez alors votre bracelet. Aucun QR code à présenter aux stands.",
  },
  {
    q: 'Je n\u2019aime pas les moules : y a-t-il une alternative ?',
    a: "Oui : nous prévoyons des saucisses à volonté pour celles et ceux qui préfèrent. Pas besoin de prévenir à l'avance — au stand, indiquez simplement votre choix au moment du service.",
  },
  {
    q: 'Je viens avec des enfants : comment ça se passe ?',
    a: "Les enfants sont les bienvenus, à un tarif réduit. Ils reçoivent un bracelet enfant distinct. Côté repas, ils peuvent picorer dans tous les stands comme les adultes (moules / saucisses / frites / dessert). Pas de menu enfant spécifique : portions adaptées à la demande.",
  },
  {
    q: 'Comment seront installées les tables ?',
    a: "Nous installerons des grandes tables (30 à 50 places). Pas de plan attribué : à votre arrivée, récupérez un marqueur au niveau des caisses pour vous installer en groupe — délimitez la zone qui vous va, avec le nombre de chaises nécessaires. On vous fait confiance !",
  },
  {
    q: 'Comment payer ?',
    a: "Sur place le jour J — espèces ou chèque (à l'ordre du Sport Quilles Séverac d'Aveyron). Le règlement se fait avant le repas : à la caisse, vous recevez un bracelet adulte ou enfant pour chaque repas payé. Vous pouvez aussi régler en plusieurs fois (un acompte, puis le solde) — pratique si tout le groupe n'arrive pas en même temps.",
  },
  {
    q: 'À quoi servent les bracelets ?',
    a: "Un bracelet par convive payé (couleur différente pour adulte et enfant). Ils sont remis à l'accueil / caisse. Le dessert se contrôle en récupérant le bracelet — simple et sans téléphone.",
  },
  {
    q: 'Allergies / régimes particuliers ?',
    a: "Pour le moment, le menu est unique (moules/saucisses + frites + dessert). Si vous avez une allergie sérieuse (crustacés, gluten…), ajoutez un commentaire lors de votre préréservation : on regardera comment vous accommoder.",
  },
  {
    q: 'Et si je dois annuler ?',
    a: "Utilisez le lien d'annulation dans l'email de confirmation, ou contactez directement le club. Plus l'annulation est anticipée, plus c'est facile : ça libère une place. Si le total repasse sous la capacité, les inscriptions en ligne rouvrent automatiquement.",
  },
  {
    q: 'Puis-je modifier mon nombre de repas ?',
    a: "Vous pouvez l'augmenter (sous réserve de la capacité). Pour le diminuer, contactez le club — on évite ainsi les erreurs de saisie. Pensez aussi à le faire avant la clôture (le 17/10/2026 à midi) pour qu'on puisse ajuster les achats.",
  },
];

export default function Infos() {
  const [cap, setCap] = useState(null);
  useEffect(() => {
    fetchCapacity().then(setCap).catch(() => setCap(null));
  }, []);

  // Adresse / horaires servent à la fois pour l'affichage et pour les liens
  // "Itinéraire" (deeplink Apple Plans / Google Maps qui marchent sans clé API).
  const address = "Salle d'Animations, avenue du Général de Gaulle, Séverac d'Aveyron";
  const mapsQuery = encodeURIComponent(address);
  const gmapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapsQuery}`;
  const applemapsUrl = `https://maps.apple.com/?q=${mapsQuery}`;

  return (
    <Container maxWidth="md" sx={{ pb: 6 }}>
      <Tooltip title="Retour à l'accueil">
        <IconButton
          component={RouterLink}
          to="/"
          size="small"
          aria-label="Retour à l'accueil"
          sx={{ mb: 1, ml: -1 }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Tooltip>

      <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.2 }}>
        Informations pratiques
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, mb: 1 }}>
        Tout savoir sur la soirée
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Soirée Moules-Frites & 50 ans du club{cap?.event_date ? ` — ${formatEventDate(cap.event_date)}` : ''}.
      </Typography>

      <Box
        component="figure"
        sx={{
          m: 0,
          mb: 3,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 12px 28px rgba(15, 35, 75, 0.14)',
        }}
      >
        <Box
          component="img"
          src="/photos/affiche.jpeg"
          alt="Affiche officielle — Les 50 ans du SQS, concert La Deryves, 17 octobre 2026"
          loading="lazy"
          sx={{
            display: 'block',
            width: '100%',
            height: 'auto',
            verticalAlign: 'middle',
          }}
        />
      </Box>

      {/* === Bloc 1 : infos clés ============================================ */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack spacing={2}>
          <Row icon={<EventOutlinedIcon color="primary" />} title="Date" value={cap?.event_date ? formatEventDate(cap.event_date) : 'Samedi 17 octobre 2026'} />
          <Row
            icon={<AccessTimeOutlinedIcon color="primary" />}
            title="Horaires"
            value="Accueil à partir de 19h00 · service du repas à partir de 20h30 · fin vers minuit"
          />
          <Row
            icon={<LocationOnOutlinedIcon color="primary" />}
            title="Lieu"
            value={
              <Stack spacing={0.5}>
                <Typography variant="body1">{address}</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button size="small" component={Link} href={gmapsUrl} target="_blank" rel="noopener noreferrer">
                    Itinéraire Google Maps
                  </Button>
                  <Button size="small" component={Link} href={applemapsUrl} target="_blank" rel="noopener noreferrer">
                    Itinéraire Apple Plans
                  </Button>
                </Stack>
              </Stack>
            }
          />
          <Row
            icon={<LocalDiningOutlinedIcon color="primary" />}
            title="Au menu"
            value="Buffet moules ou saucisses, frites à volonté, dessert maison. Pas fan des moules ? Saucisses prévues à volonté."
          />
        </Stack>
      </Paper>

      {/* === Bloc 1bis : programme / timeline de la soirée =================
          Affiché en timeline verticale (heure à gauche, libellé à droite,
          icône-spot dans la marge). Les groupes / artistes seront finalisés
          au fil des semaines : il suffira d'éditer le tableau ci-dessous. */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CelebrationOutlinedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Programme de la soirée
          </Typography>
        </Stack>
        <Stack spacing={0}>
          <TimelineRow
            time="19h00"
            title="Début de soirée"
            detail="Apéro avec animation musicale"
          />
          <TimelineRow
            time="20h30"
            title="Début du service"
            detail="Pour celles et ceux qui souhaitent se restaurer (moules / saucisses + frites, dessert à la demande)"
          />
          <TimelineRow
            time="21h45"
            title="Le mot du président"
            detail="Bref intermède pour souffler les 50 bougies du club"
          />
          <TimelineRow
            time="22h00"
            title="Concert"
            detail={
              <>
                Avec le groupe{' '}
                <Link
                  href="https://laderyves.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  sx={{ fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
                >
                  La Deryves
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </Link>
              </>
            }
            icon={<MusicNoteOutlinedIcon fontSize="small" />}
            last
          />
        </Stack>
      </Paper>

      {/* === Bloc 2 : plan d'accès + parkings =============================== */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <DirectionsCarOutlinedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Comment venir & où se garer
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          La salle d'Animations est à 100 m du parking le plus proche. Plusieurs parkings publics sont disponibles autour
          de la salle (en bleu sur le plan). Le parking en rouge est <strong>réservé aux bénévoles</strong> du club —
          merci de stationner sur l'un des 6 parkings publics.
        </Typography>
        <Box
          component="img"
          src="/plan-acces.png"
          alt="Plan d'accès à la salle d'animations avec les 6 parkings publics et le parking bénévoles"
          sx={{
            display: 'block',
            width: '100%',
            height: 'auto',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          <Chip
            icon={<DirectionsCarOutlinedIcon />}
            label="6 parkings publics autour de la salle"
            color="primary"
            variant="outlined"
          />
          <Chip
            icon={<WarningAmberOutlinedIcon />}
            label="Parking rouge réservé bénévoles"
            color="warning"
            variant="outlined"
          />
        </Stack>
      </Paper>

      {/* === Bloc 3 : FAQ =================================================== */}
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <RestaurantOutlinedIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Questions fréquentes
          </Typography>
        </Stack>
        {FAQ.map((item, i) => (
          <Accordion key={i} disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 600 }}>{item.q}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary">{item.a}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* === Bloc 4 : contact =============================================== */}
      <Alert severity="info" icon={<PhoneInTalkOutlinedIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Une question, une demande particulière ?
        </Typography>
        <Typography variant="body2">
          Répondez simplement à votre email de confirmation : un bénévole du club vous recontactera. Vous pouvez aussi
          contacter notre permanence téléphonique au{' '}
          <Link href="tel:+33781779397" underline="hover" sx={{ fontWeight: 600 }}>
            07 81 77 93 97
          </Link>{' '}
          si vous avez besoin d'une réponse rapide.
        </Typography>
      </Alert>

      <Divider sx={{ my: 3 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Button component={RouterLink} to="/" variant="contained" color="primary">
          Je préréserve mes repas
        </Button>
        <Button component={RouterLink} to="/retrouver" variant="outlined">
          Retrouver ma préréservation
        </Button>
      </Stack>
    </Container>
  );
}

function Row({ icon, title, value }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }} alignItems={{ sm: 'flex-start' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 110 }}>
        {icon}
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Box sx={{ flex: 1 }}>
        {typeof value === 'string' ? <Typography variant="body1">{value}</Typography> : value}
      </Box>
    </Stack>
  );
}

/**
 * Une étape de timeline.
 *   * heure en gros à gauche (largeur fixe pour aligner verticalement) ;
 *   * une pastille colorée + un trait vertical à droite de l'heure ;
 *   * titre/détail à droite.
 * Pas de dépendance à @mui/lab (Timeline) — on reste sur les primitives MUI core.
 */
function TimelineRow({ time, title, detail, icon, last = false }) {
  return (
    <Stack direction="row" spacing={2} alignItems="stretch">
      <Box sx={{ minWidth: 56, pt: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {time}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: 24,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 0.25,
          }}
        >
          {icon ?? <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />}
        </Box>
        {!last && (
          <Box sx={{ flex: 1, width: 2, bgcolor: 'divider', mt: 0.5, mb: 0 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, pb: last ? 0 : 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Box sx={{ color: 'text.secondary', fontSize: 14, mt: 0.25 }}>
          {typeof detail === 'string' ? detail : detail}
        </Box>
      </Box>
    </Stack>
  );
}
